import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { selectCompany } from "../../homeSlice";
import {
  badgesSlice,
  badgesListAsync,
  selectBadgesCreationStatus,
  selectBadgesListStatus,
  selectBadgesOrderedNumber,
  updateBadgeStatusAsync,
} from "./badgesSlice";
import { BadgesList } from "./badgesAPI";
import { BadgeSidePanel } from "./sidePanel/BadgeSidePanel";
import { useNavigate, useParams } from "react-router-dom";
import {
  listAllVehiclesInDbAsync,
  selectListAllVehiclesInDbStatus,
} from "../../vehicles/vehiclesSlice";
import {
  addressAsync,
  selectGetAddressStatus,
} from "../../cardsAndBadges/cards/cardsSlice";
import "../../../../i18n/i18n";
import { SharedInterfaceBadges } from "./sharedInterface";
import { triggerToast } from "@qivia/ui";
import { useTranslation } from "react-i18next";

export const Badges = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ id?: string; tab?: string }>();

  const company = useAppSelector(selectCompany);
  const badgesCreationStatus = useAppSelector(selectBadgesCreationStatus);
  const badgesOrderedNumber = useAppSelector(selectBadgesOrderedNumber);
  const badgesListStatus = useAppSelector(selectBadgesListStatus);
  const listAllVehiclesInDbStatus = useAppSelector(
    selectListAllVehiclesInDbStatus,
  );
  const getAddressStatus = useAppSelector(selectGetAddressStatus);

  useEffect(() => {
    if (company && badgesListStatus === "idle") {
      void dispatch(badgesListAsync(company.uuid));
    }
  }, [dispatch, company, badgesListStatus]);

  useEffect(() => {
    if (company && listAllVehiclesInDbStatus === "idle") {
      void dispatch(listAllVehiclesInDbAsync({ companyUuid: company.uuid }));
    }
  }, [dispatch, company, listAllVehiclesInDbStatus]);

  useEffect(() => {
    if (company && getAddressStatus === "idle") {
      void dispatch(addressAsync(company.uuid));
    }
  }, [dispatch, company, getAddressStatus]);

  useEffect(() => {
    if (badgesCreationStatus === "success" && badgesOrderedNumber) {
      badgesOrderedNumber === 1
        ? triggerToast(t("badges.creation.success.singular") || "", "valid")
        : triggerToast(
            t("badges.creation.success.plural", {
              number: badgesOrderedNumber,
            }) || "",
            "valid",
          );
      dispatch(badgesSlice.actions.resetBadgesCreationStatus());
    } else if (badgesCreationStatus === "failed") {
      triggerToast(t("badges.creation.failure") || "", "error");
      dispatch(badgesSlice.actions.resetBadgesCreationStatus());
    }
  }, [badgesCreationStatus, badgesOrderedNumber, dispatch, t]);

  const sidePanelAction = useCallback(
    (props: Partial<BadgesList>) => {
      navigate(`/home/cards/badges/${props.uuid}`);
    },
    [navigate],
  );

  const lockBadgeAction = useCallback(
    (isBadgeLocked: boolean, badgeUuid: string) => {
      void dispatch(
        updateBadgeStatusAsync({
          badgeUuid,
          status: isBadgeLocked ? "LOCKED" : "UNLOCKED",
        }),
      );
    },
    [dispatch],
  );

  if (params.tab !== "badges") {
    return;
  }

  return (
    <>
      <SharedInterfaceBadges
        sidePanel={
          <BadgeSidePanel canDeactivate lockBadgeAction={lockBadgeAction} />
        }
        sidePanelAction={sidePanelAction}
      />
    </>
  );
};
